import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Col, Row } from "react-bootstrap";
import { useFormContext } from "react-hook-form";
import { LinearProgress } from "@mui/material";
import { useQueryClient } from "react-query";
import moment from "moment";

import StepIndicator from "../../Components/Others/step-indicator";
import AddressAutoCompleteField from "../../Components/Form Fields/address-autocomplete-field";
import TextField from "../../Components/Form Fields/text-field";
import SelectField from "../../Components/Form Fields/select-field";
import { RegularAmountField } from "../../Components/Form Fields/amount-field";
import DatePickerField from "../../Components/Form Fields/date-picker-field";
import FileUploaderField from "../../Components/Form Fields/file-uploader-field";
import PrimaryButton from "../../Components/Buttons/primary-button";

import { useConsumerDealsData } from "../Deals/";

import withFormProvider from "../../HOC/withFormProvider";

import { useLogout, useUpdateLeaseInformation } from "../../CustomHooks/Mutations";

import { getUpdateLeaseInformationPayload } from "../../Utils/api-payload-generators";
import { validation } from "../../Utils/validations";
import { dispatchAPIErrorToast, dispatchErrorToast, LocalStorage, onSignOut } from "../../Utils/helpers";
import { APIQueryKeys, appUtilityKeys, USStates } from "../../Utils/constants";

import { StakeIcon, StakeLogo } from "../../Assets/Images";
import { ArrowLeft, Clock, Lock, Out } from "../../Assets/Icons";

const acceptedTypeForLeaseFile = ".pdf,.doc,.docx,.jpg,.png";

const LeaseDetails = () => {
  const { register, watch, getFieldState, formState, setValue, getValues } = useFormContext();

  const address = watch("streetAddress") ?? "";
  const state = watch("state");

  const addressProps = register("streetAddress", {
    required: "Please enter your street address",
    validate: {
      isAllowedCharacters: (address) =>
        validation.address.isAllowedCharacters(address) || "Your address must not contain any special characters",
      hasAtleast3Characters: validation.streetAddress.hasAtleast3Characters,
    },
  });

  const aptSuiteUnitProps = register("aptSuiteUnit", {
    validate: {
      isAllowedCharacters: (aptSuiteUnit) =>
        !aptSuiteUnit ||
        validation.address.isAllowedCharacters(aptSuiteUnit) ||
        "Your apt/suite number must not contain any special characters",
      hasAtmost50Characters: validation.aptSuiteUnit.hasAtmost50Characters,
    },
  });

  const cityProps = register("city", {
    required: "Please enter your city",
    validate: {
      isAllowedCharacters: (address) =>
        validation.address.isAllowedCharacters(address) || "Your city must not contain any special characters",
      hasAtleast3Characters: validation.city.hasAtleast2Characters,
    },
  });

  const stateProps = register("state", {
    required: "Please choose your state from the list",
  });

  const zipCodeProps = register("zipCode", {
    required: "Please enter your ZIP code",
    validate: validation.zipcode,
  });

  const { error: addressFieldError } = getFieldState(addressProps.name, formState);
  const { error: aptSuiteUnitFieldError } = getFieldState(aptSuiteUnitProps.name, formState);
  const { error: cityFieldError } = getFieldState(cityProps.name, formState);
  const { error: stateFieldError } = getFieldState(stateProps.name, formState);
  const { error: zipCodeFieldError } = getFieldState(zipCodeProps.name, formState);

  return (
    <>
      <AddressAutoCompleteField
        fieldProps={{
          label: "Street Address",
          placeholder: "Enter a location",
          ...addressProps,
          className: "filled",
          value: address,
          errorMessage: addressFieldError?.message,
          error: !!addressFieldError,
        }}
        onAddressSelect={(selectedAddress) => {
          setValue("streetAddress", selectedAddress.address);
          setValue("city", selectedAddress.city);
          setValue("state", selectedAddress.state);
          setValue("zipCode", selectedAddress.zipCode);
        }}
      />
      <Row className="row-gap-5">
        <Col sm={6} xs={12}>
          <TextField
            className="filled"
            label={
              <>
                Apt, Suite, Unit <span className="text-xxs-regular color--american-silver">(Optional)</span>
              </>
            }
            {...aptSuiteUnitProps}
            errorMessage={aptSuiteUnitFieldError?.message}
            error={!!aptSuiteUnitFieldError}
          />
        </Col>
        <Col sm={6} xs={12}>
          <TextField
            className="filled"
            label="City"
            {...cityProps}
            errorMessage={cityFieldError?.message}
            error={!!cityFieldError}
          />
        </Col>
        <Col sm={6} xs={12}>
          <SelectField
            className="filled"
            label="State/Province"
            options={USStates}
            {...stateProps}
            value={state ?? ""}
            errorMessage={stateFieldError?.message}
            error={!!stateFieldError}
          />
        </Col>
        <Col sm={6} xs={12}>
          <TextField
            className="filled"
            label="Zip Code"
            {...zipCodeProps}
            errorMessage={zipCodeFieldError?.message}
            error={!!zipCodeFieldError}
          />
        </Col>
      </Row>
      <RegularAmountField
        name="rentAmount"
        textFieldProps={{ className: "filled", placeholder: "1000.00" }}
        rules={{
          required: "Please enter your base rent",
        }}
      />
      <Row className="row-gap-5">
        <Col sm={6} xs={12}>
          <DatePickerField
            name="leaseStartDate"
            label="Lease Start Date"
            textFieldProps={{ placeholder: "MM / DD / YYYY", fullWidth: true }}
            required="Please pick a lease start date"
            validations={{
              isAValidDate: validation.date.isAValidDate,
            }}
            variant="filled"
            format="MM / DD / YYYY"
          />
        </Col>
        <Col sm={6} xs={12}>
          <DatePickerField
            name="leaseEndDate"
            label="Lease End Date"
            textFieldProps={{ placeholder: "MM / DD / YYYY", fullWidth: true }}
            datePickerProps={{ disablePast: true }}
            required="Please pick a lease end date"
            validations={{
              isAValidDate: validation.date.isAValidDate,
              isGreaterThanLeaseStartDate: (leaseEndDate) =>
                moment(leaseEndDate) > moment(getValues("leaseStartDate")) ||
                "Lease end date must be after the start date",
            }}
            variant="filled"
            format="MM / DD / YYYY"
          />
        </Col>
      </Row>
    </>
  );
};

const LeaseUpload = () => {
  const { setValue, watch } = useFormContext();

  const leaseFiles = watch("leaseFiles") ?? [];

  const leaseFilesFieldValidations = {
    fileSize: (files) => {
      if (files[0]?.size < 10_000_000) {
        return true;
      } else {
        setValue("leaseFiles", null);
        dispatchErrorToast("This file is too large. Please upload a file under 10MB.", {
          key: "leaseFileSize",
        });
        return false;
      }
    },
    fileFormat: (files) => {
      const fileName = files[0].name;
      const fileExtension = fileName.split(".").pop().toLowerCase();
      if (acceptedTypeForLeaseFile.replaceAll(".", "").split(",").includes(fileExtension)) {
        return true;
      } else {
        setValue("leaseFiles", null);
        dispatchErrorToast("File type not accepted. Please upload a supported file format.", {
          key: "leaseFileType",
        });
        return false;
      }
    },
  };

  return (
    <div className="flex-grow-1">
      <div className="upload-lease-title">Upload your signed lease</div>
      <p className="upload-lease-description">
        Upload a signed copy of your lease so we can verify your residence and activate your renter rewards.
      </p>
      <FileUploaderField
        name="leaseFiles"
        title="Upload your signed lease"
        accept={acceptedTypeForLeaseFile}
        maxSize={10000000}
        files={leaseFiles}
        onFileRemove={(index) => {
          const tempFiles = [...leaseFiles];
          tempFiles.splice(index, 1);
          setValue("leaseFiles", tempFiles);
        }}
        rules={{
          validate: leaseFilesFieldValidations,
        }}
      />
    </div>
  );
};

function SetupAccount() {
  const [step, setStep] = useState(1);
  const [uploadProgress, setUploadProgress] = useState(0);

  const { watch, handleSubmit } = useFormContext();

  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const userEmail = useMemo(() => LocalStorage.read(appUtilityKeys.loggedInUserData)?.email, []);

  const leaseFiles = watch("leaseFiles") ?? [];

  const { stakeSaverOffer } = useConsumerDealsData();

  const { mutate: InitLogout } = useLogout({
    onSettled: onSignOut,
  });

  const { isLoading: isLoadingUpdateLeaseInformation, mutate: UpdateLeaseInformation } = useUpdateLeaseInformation({
    onSuccess: () => {
      queryClient.refetchQueries(APIQueryKeys.deals);
      navigate(`/deals${!!stakeSaverOffer ? "/stake-saver" : ""}`);
    },
    onError: (err) => dispatchAPIErrorToast({ message: err?.response?.data?.error, key: "updateLeaseInformation" }),
  });

  const onUploadProgress = (progressEvent) => {
    setUploadProgress((progressEvent.loaded / progressEvent.total) * 100);
  };

  const onSubmit = (formData) => {
    if (step === 1) {
      setStep(2);
    } else if (step === 2) {
      UpdateLeaseInformation({ data: getUpdateLeaseInformationPayload(formData), config: { onUploadProgress } });
    }
  };

  return (
    <div className="onboarding-page setup-account sso-page">
      {!isLoadingUpdateLeaseInformation ? (
        <>
          <header className="bordered">
            <StakeLogo className="stake-logo" />
            <button className="sign-out-button" onClick={InitLogout}>
              <Out />
              Sign Out <span className="user-cred">({userEmail})</span>
            </button>
          </header>
          <div className="max-width-container">
            <h4 className="page-title">Let's get started</h4>
            <p className="page-description">
              <Clock />2 minutes to complete
            </p>
            <form className="registration-form" onSubmit={handleSubmit(onSubmit)} autoComplete="off">
              <StepIndicator
                activeStep={step}
                hasLabels={true}
                steps={2}
                stepLabels={["Lease details", "Lease upload"]}
              />
              {step === 1 ? <LeaseDetails /> : step === 2 ? <LeaseUpload /> : <></>}
              <div className="d-flex align-items-center column-gap-4">
                {step > 1 && (
                  <button className="back-button" type="button" onClick={setStep.bind(this, (p) => p - 1)}>
                    <ArrowLeft />
                  </button>
                )}
                <PrimaryButton className="flex-grow-1" type="submit" disabled={step === 2 && leaseFiles.length === 0}>
                  Continue
                </PrimaryButton>
              </div>
              <div className="data-secure-label">
                <Lock />
                Your personal data is encrypted and stored securely
              </div>
            </form>
            <div className="data-secure-label mt-4 mb-7">
              <Lock />
              Your personal data is encrypted and stored securely
            </div>
          </div>
        </>
      ) : (
        <div className="setting-up-account">
          <div className="icon-wrapper">
            <StakeIcon className="stake-icon" />
          </div>
          <h3 className="loader-title">Setting up your account</h3>
          <p className="loader-description">
            Join thousands of renters who are building credit and earn rewards with every payment.
          </p>
          <LinearProgress variant="determinate" value={uploadProgress} />
        </div>
      )}
    </div>
  );
}

export default withFormProvider(SetupAccount);
